import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import './Notification.css';

const Notification = ({
  show,
  message,
  type = 'info',
  autoClose = true,
  autoCloseDuration = 5000,
  onClose
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef();
  const animationRef = useRef();

  const handleClose = useCallback(() => {
    setIsVisible(false);
    if (onClose) {
      // Delay the onClose callback to allow exit animation to complete
      setTimeout(() => onClose(), 300);
    }
  }, [onClose]);

  // Handle mount/unmount based on show prop
  useEffect(() => {
    if (show) {
      setIsMounted(true);
      // Small delay to ensure DOM is ready before showing
      animationRef.current = setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      // Delay unmount to allow exit animation to complete
      animationRef.current = setTimeout(() => setIsMounted(false), 300);
    }

    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(animationRef.current);
    };
  }, [show]);

  // Handle auto-close timer
  useEffect(() => {
    if (isVisible && autoClose) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(handleClose, autoCloseDuration);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [isVisible, autoClose, autoCloseDuration, handleClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="notification-icon" />;
      case 'error':
        return <AlertCircle className="notification-icon" />;
      case 'warning':
        return <AlertCircle className="notification-icon" />;
      default:
        return <Info className="notification-icon" />;
    }
  };

  if (!isMounted) return null;

  return (
    <div className={`notification ${type} ${isVisible ? 'visible' : ''}`}>
      <div className="notification-content">
        <div className="notification-header">
          {getIcon()}
          <span className="notification-message">{message}</span>
        </div>
        <button
          className="notification-close-btn"
          onClick={handleClose}
          aria-label="Close notification"
        >
          <X className="notification-close-icon" />
        </button>
      </div>
      
      {autoClose && (
        <div className="notification-progress-bg">
          <div 
            className={`notification-progress-line ${type} ${isVisible ? 'animating' : ''}`}
            style={{
              animationDuration: `${autoCloseDuration}ms`
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Notification;